'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useQueryClient } from '@tanstack/react-query';
import { trackWalletAction } from '@workspace/analytics/posthog/events';
import { Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useBatchTransferRealtime } from '@/hooks/use-batch-transfer-realtime';
import { getFailedBatchTransfers } from '@/hooks/use-batch-transfers';
import type { BatchTransferScaData } from '@/types/batch-transfer-realtime';
import {
  BATCH_TRANSFER_COMPLETED_EVENT,
  BATCH_TRANSFER_ERROR_EVENT,
  BATCH_TRANSFER_PARTIAL_EVENT,
  type BatchTransferStartEventDetail,
} from '@/types/batch-transfer-wizard-events';

const TOAST_ID = 'batch-transfer-progress';
const TIMEOUT_MS = 30_000;
const PROGRESS_TOAST_CLASS_NAME = 'items-start md:min-w-[610px]';
const PARTIAL_SUCCESS_COPY =
  'Some payments have been processed successfully, while others have been queued due to insufficient funds.';
const PROGRESS_TOAST_ICON = (
  <div className="flex size-5 shrink-0 items-start p-0.5">
    <Info size={16} className="stroke-2! text-blue-500 dark:text-blue-400" />
  </div>
);

type BatchTransferOutcome = 'success' | 'partial' | 'failure';

interface ClassifyOutcomeParams {
  fromEntity: boolean;
  totalPayments: number;
}

interface ClassifyOutcomeResult {
  outcome: BatchTransferOutcome;
  queuedCount: number;
}

async function classifyOutcome({
  fromEntity,
  totalPayments,
}: ClassifyOutcomeParams): Promise<ClassifyOutcomeResult> {
  const result = await getFailedBatchTransfers({ from_entity: fromEntity });
  const queuedCount = result.data.reduce((sum, transfer) => sum + transfer.data.length, 0);

  if (queuedCount === 0) {
    return { outcome: 'success', queuedCount };
  }

  if (fromEntity && totalPayments > 0 && queuedCount < totalPayments) {
    return { outcome: 'partial', queuedCount };
  }

  return { outcome: 'failure', queuedCount };
}

function dispatchOutcome(
  outcome: BatchTransferOutcome,
  options: { queuedCount: number; errorMessage?: string }
): void {
  if (outcome === 'success') {
    trackWalletAction('batch_transfer_completed', {
      flow: 'batch_transfer',
      result: 'success',
    });
    toast.success('Your batch transfer has been completed successfully.');
    window.dispatchEvent(new CustomEvent(BATCH_TRANSFER_COMPLETED_EVENT));
    return;
  }

  if (outcome === 'partial') {
    trackWalletAction('batch_transfer_completed', {
      flow: 'batch_transfer',
      result: 'success',
      count: options.queuedCount,
    });
    toast.warning(PARTIAL_SUCCESS_COPY);
    window.dispatchEvent(
      new CustomEvent(BATCH_TRANSFER_PARTIAL_EVENT, {
        detail: { queuedCount: options.queuedCount },
      })
    );
    return;
  }

  trackWalletAction('batch_transfer_failed', {
    flow: 'batch_transfer',
    result: 'failure',
    count: options.queuedCount,
  });
  const message =
    options.errorMessage || 'Your batch transfer has failed due to insufficient funds.';
  toast.error(message);
  window.dispatchEvent(
    new CustomEvent(BATCH_TRANSFER_ERROR_EVENT, {
      detail: { message },
    })
  );
}

function BatchTransferProgressToastContent({ progress }: { progress: number }) {
  return (
    <div className="flex w-full flex-col gap-3">
      <p>Batch transfer in progress.</p>
      <div className="w-full">
        <div className="h-2.5 w-full overflow-hidden rounded-[20px] bg-grey-200 dark:bg-grey-900">
          <div
            className="h-full rounded-[20px] bg-pink-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function invalidateBatchTransferQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
  queryClient.invalidateQueries({ queryKey: ['wallets'] });
  queryClient.invalidateQueries({ queryKey: ['transactions'] });
  queryClient.invalidateQueries({ queryKey: ['failed-batch-transfers'] });
}

/**
 * Resolves the batch transfer outcome by checking the failed transfers API,
 * then shows the appropriate toast and invalidates queries.
 */
function resolveOutcome(
  queryClient: ReturnType<typeof useQueryClient>,
  cleanup: () => void,
  fromEntity: boolean,
  totalPayments: number
): void {
  toast.dismiss(TOAST_ID);
  invalidateBatchTransferQueries(queryClient);

  classifyOutcome({ fromEntity, totalPayments })
    .then(({ outcome, queuedCount }) => {
      dispatchOutcome(outcome, {
        queuedCount,
        errorMessage: fromEntity
          ? undefined
          : 'Your batch transfer has failed due to insufficient funds.',
      });
    })
    .catch(() => {
      dispatchOutcome('failure', {
        queuedCount: 0,
        errorMessage: 'Your batch transfer has failed.',
      });
    })
    .finally(cleanup);
}

/**
 * Component that manages the batch transfer progress toast lifecycle.
 * Renders nothing visible itself -- the toast is rendered by Sonner at the layout level.
 *
 * Handles two scenarios:
 * - Realtime completion event received -> resolve immediately
 * - No events received within TIMEOUT_MS -> assume silent failure, resolve via API check
 */
export function BatchTransferProgressToast({ userId }: { userId: number | null }) {
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const queryClient = useQueryClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolvedRef = useRef(false);
  const fromEntityRef = useRef(false);
  const totalPaymentsRef = useRef(0);

  const cleanup = useCallback(() => {
    resolvedRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActive(false);
    setToastVisible(false);
    setProgress(0);
  }, []);

  const handleCompleted = useCallback(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    resolveOutcome(queryClient, cleanup, fromEntityRef.current, totalPaymentsRef.current);
  }, [queryClient, cleanup]);

  const handleError = useCallback(
    (message: string) => {
      invalidateBatchTransferQueries(queryClient);

      if (resolvedRef.current) return;
      resolvedRef.current = true;
      toast.dismiss(TOAST_ID);

      if (!fromEntityRef.current) {
        dispatchOutcome('failure', {
          queuedCount: 0,
          errorMessage: message || 'Your batch transfer has failed.',
        });
        cleanup();
        return;
      }

      classifyOutcome({ fromEntity: true, totalPayments: totalPaymentsRef.current })
        .then(({ outcome, queuedCount }) => {
          dispatchOutcome(outcome, { queuedCount, errorMessage: message });
        })
        .catch(() => {
          dispatchOutcome('failure', { queuedCount: 0, errorMessage: message });
        })
        .finally(cleanup);
    },
    [queryClient, cleanup]
  );

  const handleSca = useCallback(
    (data: BatchTransferScaData) => {
      console.log(
        '[SCA Progress Toast] handleSca called with data:',
        JSON.stringify(data, null, 2)
      );

      if (!data.requires_sca) {
        console.log('[SCA Progress Toast] requires_sca is false, skipping SCA');
        // No SCA required, show the toast now
        setToastVisible(true);
        return;
      }

      toast.dismiss(TOAST_ID);

      if (!data.encrypted_payment_id) {
        console.error('[SCA Progress Toast] No encrypted_payment_id in SCA data');
        toast.error('Batch transfer creation failed in EPS. Please try again.');
        window.dispatchEvent(
          new CustomEvent(BATCH_TRANSFER_ERROR_EVENT, {
            detail: { message: 'Batch transfer creation failed in EPS. Please try again.' },
          })
        );
        cleanup();
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setToastVisible(false);

      const eventDetail = {
        encrypted_payment_id: data.encrypted_payment_id,
        realm_id: data.realm_id,
        sca_iframe_src: data.sca_iframe_src,
        sca_iframe_target_origin: data.sca_iframe_target_origin,
      };
      console.log(
        '[SCA Progress Toast] Dispatching batch-transfer-sca event:',
        JSON.stringify(eventDetail, null, 2)
      );

      window.dispatchEvent(new CustomEvent('batch-transfer-sca', { detail: eventDetail }));
    },
    [cleanup]
  );

  // Start/clear timeout whenever active state changes
  useEffect(() => {
    if (!active) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (!resolvedRef.current) {
        resolvedRef.current = true;
        resolveOutcome(queryClient, cleanup, fromEntityRef.current, totalPaymentsRef.current);
      }
    }, TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [active, queryClient, cleanup]);

  useEffect(() => {
    const handleActivate = (event: Event) => {
      resolvedRef.current = false;
      setActive(true);
      setProgress(0);

      const detail = (event as CustomEvent<BatchTransferStartEventDetail>).detail;
      fromEntityRef.current = detail?.fromEntity === true;
      totalPaymentsRef.current =
        typeof detail?.totalPayments === 'number' ? detail.totalPayments : 0;

      if (detail?.showToast) {
        setToastVisible(true);
      }
    };

    window.addEventListener('batch-transfer-start', handleActivate);
    return () => window.removeEventListener('batch-transfer-start', handleActivate);
  }, []);

  const handleProgress = useCallback((percent: number) => {
    setProgress(percent);
    // Show toast when we receive progress updates (after SCA is completed)
    setToastVisible(true);
  }, []);

  useBatchTransferRealtime({
    userId,
    enabled: active && !!userId,
    onProgress: handleProgress,
    onCompleted: handleCompleted,
    onError: handleError,
    onSca: handleSca,
  });

  useEffect(() => {
    if (!toastVisible) return;

    toast.info(<BatchTransferProgressToastContent progress={progress} />, {
      id: TOAST_ID,
      duration: Infinity,
      className: PROGRESS_TOAST_CLASS_NAME,
      icon: PROGRESS_TOAST_ICON,
    });
  }, [progress, toastVisible]);

  return null;
}

/**
 * Imperative function to show the batch transfer progress toast.
 * Returns a stable toast ID that can be used to dismiss it.
 */
export function showBatchTransferProgressToast() {
  toast.info(<BatchTransferProgressToastContent progress={0} />, {
    id: TOAST_ID,
    duration: Infinity,
    className: PROGRESS_TOAST_CLASS_NAME,
    icon: PROGRESS_TOAST_ICON,
  });

  window.dispatchEvent(new CustomEvent('batch-transfer-start'));

  return TOAST_ID;
}
